
export type LevelFailurePolicy = 'block' | 'continue' | 'advisory';

export interface LevelPolicyFields {
  isOptional: boolean;
  onReject: 'block' | 'continue';
}

export function levelFailurePolicy(level: LevelPolicyFields): LevelFailurePolicy {
  if (level.isOptional) return 'advisory';
  return level.onReject === 'continue' ? 'continue' : 'block';
}

export function levelPolicyFields(policy: LevelFailurePolicy): LevelPolicyFields {
  if (policy === 'advisory') return { isOptional: true, onReject: 'continue' };
  return { isOptional: false, onReject: policy };
}

export const LEVEL_POLICY_OPTIONS: {
  value: LevelFailurePolicy;
  label: string;
  hint: string;
}[] = [
  {
    value: 'block',
    label: 'Заявку отклонить сразу',
    hint: 'Остальные уровни не дожидаемся — их запросы снимаются как ненужные',
  },
  {
    value: 'continue',
    label: 'Заявку отклонить, но дать высказаться остальным',
    hint: 'Другие уровни продолжат согласование, итог круга всё равно «не согласовано»',
  },
  {
    value: 'advisory',
    label: 'Ничего — уровень совещательный',
    hint: 'Мнение спросим, но заявку можно согласовать и без этого уровня',
  },
];

export function levelFailureSentence(policy: LevelFailurePolicy): string {
  switch (policy) {
    case 'continue':
      return 'Не наберётся — заявку отклоним, когда выскажутся остальные уровни.';
    case 'advisory':
      return 'Не наберётся — на итог не влияет: заявку можно согласовать и без этого уровня.';
    case 'block':
    default:
      return 'Не наберётся — заявку отклоним сразу, не дожидаясь других уровней.';
  }
}

export function levelPolicyNote(
  level: LevelPolicyFields,
  outcome?: 'pending' | 'passed' | 'failed',
): { label: string; hint: string } | null {
  const policy = levelFailurePolicy(level);
  if (policy === 'advisory') {
    return {
      label: 'не влияет на итог',
      label2: undefined,
      hint: 'Совещательный уровень: заявку можно согласовать и без него',
    } as { label: string; hint: string };
  }
  if (policy === 'continue' && outcome === 'failed') {
    return {
      label: 'отказ после всех',
      hint: 'Кворум недостижим — заявку отклоним, когда выскажутся остальные уровни',
    };
  }
  return null;
}


export type LevelQuorumMode = 'all' | 'any' | 'count' | 'percent';

export interface LevelQuorumFields {
  quorumMode: LevelQuorumMode;
  quorumValue: number | null;
  membersCount: number;
}

export function requiredApprovals(level: LevelQuorumFields): number {
  const total = level.membersCount;
  if (total <= 0) return 0;
  switch (level.quorumMode) {
    case 'any':
      return 1;
    case 'count':
      return Math.min(Math.max(1, level.quorumValue ?? 1), total);
    case 'percent':
      return Math.max(1, Math.ceil((total * Math.min(100, Math.max(1, level.quorumValue ?? 100))) / 100));
    case 'all':
    default:
      return total;
  }
}

export function quorumSentence(level: LevelQuorumFields): string {
  const total = level.membersCount;
  if (total === 0) return 'В уровне нет согласующих — круг с ним не запустится';
  switch (level.quorumMode) {
    case 'any':
      return `Достаточно решения одного из ${total}`;
    case 'count':
      return `Нужно ${requiredApprovals(level)} из ${total}`;
    case 'percent':
      return `Нужно ${requiredApprovals(level)} из ${total} (${level.quorumValue ?? 100}%)`;
    case 'all':
    default:
      return total === 1 ? 'Нужно решение единственного согласующего' : `Нужны все ${total}`;
  }
}

export function quorumShort(level: LevelQuorumFields): string {
  const total = level.membersCount;
  if (total === 0) return 'пусто';
  switch (level.quorumMode) {
    case 'any':
      return `любой из ${total}`;
    case 'all':
      return total === 1 ? 'нужен один' : `нужны все ${total}`;
    default:
      return `нужно ${requiredApprovals(level)} из ${total}`;
  }
}

export function committeeStructureText(
  levels: (LevelQuorumFields & { name: string; isOptional: boolean })[],
): string {
  if (!levels.length) return 'В комитете нет уровней — согласовывать некому';
  return levels
    .map((l) => `${l.name} — ${quorumShort(l)}${l.isOptional ? ', не влияет на итог' : ''}`)
    .join(' · ');
}
